import { useAppState } from "@hooks/useAppState"
import { useHydrating } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { PropsWithChildren } from "react"
import { createPortal } from "react-dom"

type Props = PropsWithChildren<{
  enabled?: boolean
}>

/**
 * A utility layout used to present content anchored to the bottom of the screen.
 * Fades out when the footer is visible, and animates slightly when the menu is opened.
 */
export function FloatingTabContainer(props: Props) {
  const hydrating = useHydrating()
  const state = useAppState()
  const { servicesVisible } = useAppState()

  const shouldShow = props.enabled !== false && !state.footerVisible && !servicesVisible

  if (hydrating) return null

  return createPortal(
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          variants={{
            hide: { opacity: 0, y: 30 },
            show: { opacity: 1, y: 0 },
            menuOpen: { opacity: 1, y: -30, scale: 0.9, transition: { duration: 0.6 } },
          }}
          initial="hide"
          animate={state.menuIsOpen ? "menuOpen" : "show"}
          exit={{ opacity: 0, y: 30, filter: "blur(20px)" }}
          className="fixed flex items-center justify-center bottom-8 md:bottom-10 left-3 right-3 z-30 will-change-transform pointer-events-none"
        >
          <div className="pointer-events-auto">{props.children}</div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
